import { useEffect, useRef } from 'react';
import { useWeb5Context } from 'lib/Web5Context';

let horizontalResizeActive = false;

const setHorizontalResizeActiveGlobal = (isActive) => {
    horizontalResizeActive = isActive;
};

export const isHorizontalResizeActive = () => horizontalResizeActive;

export const useIsHorizontalResizeActive = () => {
    const { isHorizontalResizeActive: contextIsActive = false } = useWeb5Context();
    return contextIsActive;
};

const useHorizontalResize = ({
    targetRef,
    containerRef,
    edgeWidth = 3,
    minWidth = 350,
    minPercentage = 30,
    maxPercentage = 70,
    highlightColor = 'var(--actionMajor-500)',
    onResizeStart,
    onResize,
    onResizeEnd,
    enabled = true,
}) => {
    const { setHorizontalResizeActive } = useWeb5Context();
    const isResizing = useRef(false);
    const isHovering = useRef(false);
    const hasTriggeredStart = useRef(false);
    const rafId = useRef(null);
    const overlayRef = useRef(null);
    const pointerIdRef = useRef(null);
    const dragStartX = useRef(0);
    const dragStartWidth = useRef(0);
    const dragContainerWidth = useRef(0);
    const lastResizeResult = useRef(null);

    useEffect(() => {
        const target = targetRef.current;
        const container = containerRef.current;
        if (!enabled || !target || !container) return;

        const overlay = document.createElement('div');
        overlay.className = 'fm-resize-handle';
        overlay.style.cssText = `
            position: absolute;
            top: 0;
            right: 0;
            width: ${edgeWidth}px;
            height: 100%;
            cursor: ew-resize;
            z-index: 100;
        `;
        target.appendChild(overlay);
        overlayRef.current = overlay;

        const applyHighlight = () => {
            target.style.boxShadow = `inset -2px 0 0 ${highlightColor}`;
        };

        const removeHighlight = () => {
            target.style.boxShadow = '';
        };

        const setActive = (isActive) => {
            setHorizontalResizeActiveGlobal(isActive);
            setHorizontalResizeActive(isActive);
        };

        const emitResize = (percentageList) => {
            const roundedList = Number(percentageList.toFixed(4));
            const roundedDetail = Number((100 - roundedList).toFixed(4));

            if (
                lastResizeResult.current &&
                lastResizeResult.current.percentageList === roundedList &&
                lastResizeResult.current.percentageDetail === roundedDetail
            ) {
                return;
            }

            lastResizeResult.current = {
                percentageList: roundedList,
                percentageDetail: roundedDetail,
            };
            onResize?.(lastResizeResult.current);
        };

        const finishResize = () => {
            if (!isResizing.current) return;

            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
                rafId.current = null;
            }

            const totalWidth = dragContainerWidth.current || container.getBoundingClientRect().width;
            if (totalWidth) {
                const currentWidth = target.getBoundingClientRect().width;
                let percentageList = (100 / totalWidth) * currentWidth;
                if (percentageList < minPercentage) percentageList = minPercentage;
                if (percentageList > maxPercentage) percentageList = maxPercentage;
                emitResize(percentageList);
            }

            if (onResizeEnd && lastResizeResult.current) {
                onResizeEnd(lastResizeResult.current);
            }

            isResizing.current = false;
            setActive(false);
            pointerIdRef.current = null;
            dragStartX.current = 0;
            dragStartWidth.current = 0;
            dragContainerWidth.current = 0;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            if (!isHovering.current) {
                removeHighlight();
            }
        };

        const handlePointerEnter = () => {
            isHovering.current = true;
            applyHighlight();
        };

        const handlePointerLeave = () => {
            isHovering.current = false;
            if (!isResizing.current) {
                removeHighlight();
            }
        };

        const handlePointerDown = (e) => {
            if (e.button !== 0) return;

            isResizing.current = true;
            setActive(true);
            pointerIdRef.current = e.pointerId;
            dragStartX.current = e.clientX;
            dragStartWidth.current = target.getBoundingClientRect().width;
            dragContainerWidth.current = container.getBoundingClientRect().width;
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
            applyHighlight();

            if (overlay.setPointerCapture) {
                overlay.setPointerCapture(e.pointerId);
            }

            e.preventDefault();
            e.stopPropagation();

            if (!hasTriggeredStart.current) {
                hasTriggeredStart.current = true;
                onResizeStart?.();
            }
        };

        const handlePointerMove = (e) => {
            if (!isResizing.current) return;
            if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) return;

            if (rafId.current) cancelAnimationFrame(rafId.current);

            rafId.current = requestAnimationFrame(() => {
                const totalWidth = dragContainerWidth.current;
                if (!totalWidth) return;
                const deltaX = e.clientX - dragStartX.current;
                let newWidth = dragStartWidth.current + deltaX;

                if (newWidth < minWidth) newWidth = minWidth;

                let percentageList = (100 / totalWidth) * newWidth;

                if (percentageList < minPercentage) {
                    percentageList = minPercentage;
                } else if (percentageList > maxPercentage) {
                    percentageList = maxPercentage;
                }

                target.style.width = `${percentageList}%`;
                emitResize(percentageList);
            });
        };

        const handlePointerUp = (e) => {
            if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current) return;

            if (overlay.releasePointerCapture && overlay.hasPointerCapture?.(e.pointerId)) {
                overlay.releasePointerCapture(e.pointerId);
            }

            finishResize();
        };

        const handleLostPointerCapture = () => finishResize();
        const handleWindowPointerUp = () => finishResize();
        const handleWindowPointerCancel = () => finishResize();
        const handleVisibilityChange = () => {
            if (document.hidden) {
                finishResize();
            }
        };

        overlay.addEventListener('pointerenter', handlePointerEnter);
        overlay.addEventListener('pointerleave', handlePointerLeave);
        overlay.addEventListener('pointerdown', handlePointerDown);
        overlay.addEventListener('pointermove', handlePointerMove);
        overlay.addEventListener('pointerup', handlePointerUp);
        overlay.addEventListener('pointercancel', handlePointerUp);
        overlay.addEventListener('lostpointercapture', handleLostPointerCapture);
        window.addEventListener('pointerup', handleWindowPointerUp, true);
        window.addEventListener('pointercancel', handleWindowPointerCancel, true);
        window.addEventListener('blur', finishResize);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            overlay.removeEventListener('pointerenter', handlePointerEnter);
            overlay.removeEventListener('pointerleave', handlePointerLeave);
            overlay.removeEventListener('pointerdown', handlePointerDown);
            overlay.removeEventListener('pointermove', handlePointerMove);
            overlay.removeEventListener('pointerup', handlePointerUp);
            overlay.removeEventListener('pointercancel', handlePointerUp);
            overlay.removeEventListener('lostpointercapture', handleLostPointerCapture);
            window.removeEventListener('pointerup', handleWindowPointerUp, true);
            window.removeEventListener('pointercancel', handleWindowPointerCancel, true);
            window.removeEventListener('blur', finishResize);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (rafId.current) cancelAnimationFrame(rafId.current);
            finishResize();
            setActive(false);
            removeHighlight();
            lastResizeResult.current = null;
            if (overlayRef.current && target.contains(overlayRef.current)) {
                target.removeChild(overlayRef.current);
            }
        };
    }, [
        targetRef,
        containerRef,
        edgeWidth,
        minWidth,
        minPercentage,
        maxPercentage,
        highlightColor,
        onResizeStart,
        onResize,
        onResizeEnd,
        enabled,
        setHorizontalResizeActive,
    ]);
};

export default useHorizontalResize;
